import { useMemo } from "react"

import { usePreorderStockWalk, type StockWalkEvent } from "~/hooks/usePreorderStockWalk"
import { useSkuStore } from "~/providers/sku-store-provider"
import { startOfTodayUTC } from "~/utils/datetime"
import {
  getPreorderBoundaries,
  getTimingCategory,
  getTimingLabel,
  type PreorderTimingCategory,
} from "~/utils/unfulfillablePreorderUtils"
import type {
  PreorderMatrixRow,
  PreorderMatrixTotals,
} from "~/components/reusable/cards/PreorderMatrix"

const TIMING_ORDER: PreorderTimingCategory[] = [
  "within_leadtime",
  "within_purchase_interval",
  "outside",
]

type Options = {
  skuIds?: string[]
  dateRange?: { start: Date; end: Date }
}

// Row shape returned by this hook, exported so callers can name it without respelling the
// ReturnType expression. Has no importer yet; the preorder shortfall UI is still being built.
// fallow-ignore-next-line unused-type
export type ShortfallRow = ReturnType<typeof usePreorderShortfallRows>["shortfallRows"][number]
export type { StockWalkEvent }

export function usePreorderShortfallRows(
  variantIds: string[],
  { skuIds, dateRange }: Options = {},
) {
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const channel = useSkuStore((state) => state.channel)
  const variantMap = useSkuStore((state) => state.newVariantMap)
  const today = useMemo(() => startOfTodayUTC(), [])

  const {
    preorders: allWarehousePreorders,
    availableAtDeliveryMap,
    skuStockMap,
    sortedEvents,
  } = usePreorderStockWalk(variantIds, warehouseId || undefined)

  const variantSettingsMap = useMemo(() => {
    const m = new Map<string, { totalLeadTimeDays: number; purchaseIntervalDays: number }>()
    for (const id of variantIds) {
      const v = variantMap.get(id)
      if (v) {
        m.set(id, {
          totalLeadTimeDays: v.settings?.totalLeadTimeDays ?? 30,
          purchaseIntervalDays: v.settings?.purchaseIntervalDays ?? 35,
        })
      }
    }
    return m
  }, [variantIds, variantMap])

  const filteredPreorders = useMemo(
    () =>
      allWarehousePreorders.filter((po) => {
        if (channel && po.channel !== channel) return false
        if (skuIds && !skuIds.includes(po.skuId)) return false
        if (dateRange) {
          const t = po.deliveryDate.getTime()
          if (t < dateRange.start.getTime() || t > dateRange.end.getTime()) return false
        }
        return true
      }),
    [allWarehousePreorders, channel, skuIds, dateRange],
  )

  const shortfallRows = useMemo(
    () =>
      filteredPreorders
        .filter((po) => po.atRiskQty > 0 || po.unfulfillableQty > 0)
        .map((po) => {
          const s = variantSettingsMap.get(po.variantId)
          const totalLeadTimeDays = s?.totalLeadTimeDays ?? 30
          const purchaseIntervalDays = s?.purchaseIntervalDays ?? 35
          const timing = getTimingCategory(
            po.deliveryDate,
            today,
            totalLeadTimeDays,
            purchaseIntervalDays,
          )
          const boundaries = getPreorderBoundaries(today, totalLeadTimeDays, purchaseIntervalDays)
          return { ...po, timing, boundaries }
        })
        .sort((a, b) => {
          const dateCmp = a.deliveryDate.getTime() - b.deliveryDate.getTime()
          if (dateCmp !== 0) return dateCmp
          const skuCmp = a.skuId.localeCompare(b.skuId)
          if (skuCmp !== 0) return skuCmp
          return a.quantity - b.quantity
        }),
    [filteredPreorders, today, variantSettingsMap],
  )

  const matrixRows = useMemo<PreorderMatrixRow[]>(() => {
    const byTiming = new Map<PreorderTimingCategory, PreorderMatrixRow>()
    for (const timing of TIMING_ORDER) {
      byTiming.set(timing, {
        timing,
        label: getTimingLabel(timing),
        unfulfillable: 0,
        atRisk: 0,
        fulfillable: 0,
        total: 0,
      })
    }
    for (const po of filteredPreorders) {
      const s = variantSettingsMap.get(po.variantId)
      const timing = getTimingCategory(
        po.deliveryDate,
        today,
        s?.totalLeadTimeDays ?? 30,
        s?.purchaseIntervalDays ?? 35,
      )
      const row = byTiming.get(timing)!
      row.unfulfillable += po.unfulfillableQty
      row.atRisk += po.atRiskQty
      row.fulfillable += Math.max(0, po.quantity - po.unfulfillableQty - po.atRiskQty)
      row.total += po.quantity
    }
    return TIMING_ORDER.map((t) => byTiming.get(t)!).filter((r) => r.total > 0)
  }, [filteredPreorders, today, variantSettingsMap])

  const matrixTotals = useMemo<PreorderMatrixTotals>(
    () =>
      matrixRows.reduce(
        (acc, r) => ({
          unfulfillable: acc.unfulfillable + r.unfulfillable,
          atRisk: acc.atRisk + r.atRisk,
          fulfillable: acc.fulfillable + r.fulfillable,
          total: acc.total + r.total,
        }),
        { unfulfillable: 0, atRisk: 0, fulfillable: 0, total: 0 },
      ),
    [matrixRows],
  )

  return {
    shortfallRows,
    matrixRows,
    matrixTotals,
    availableAtDeliveryMap,
    skuStockMap,
    sortedEvents,
  }
}
